/**
 * Group @mentions — parse display-name tokens (SPEC §13 mentions). Same rules as the phone
 * app (apps/mobile/lib/src/messaging/formatting.dart): one token, Unicode letters, trailing
 * punctuation isn't part of the name, and an e-mail address (letter right before the @) is
 * not a mention. A member is mentioned by their name without spaces ("Jean Martial" →
 * "@JeanMartial") or by its first word ("@Jean").
 */

export interface MentionHit {
  displayName: string;
  start: number;
  end: number;
}

const MENTION_RE = /(?<![\p{L}\p{N}])@([\p{L}\p{N}][\p{L}\p{N}._'-]{0,31})/gu;

const trimMention = (t: string): string => t.replace(/[._'-]+$/u, "");

export function extractMentions(body: string): MentionHit[] {
  const hits: MentionHit[] = [];
  for (const m of body.matchAll(MENTION_RE)) {
    const name = trimMention(m[1]!);
    if (!name) continue;
    hits.push({ displayName: name, start: m.index!, end: m.index! + 1 + name.length });
  }
  return hits;
}

/** How a member is mentioned: their name without spaces, at most 32 characters. */
export function mentionToken(displayName: string): string {
  const t = displayName.replace(/[^\p{L}\p{N}._'-]/gu, "");
  return trimMention(t.length > 32 ? t.slice(0, 32) : t);
}

/** True when [body] @mentions any of [names] — by the whole name or its first word. */
export function mentionsAnyName(body: string, names: Iterable<string>): boolean {
  const want = new Set<string>();
  for (const n of names) {
    const whole = mentionToken(n);
    const first = mentionToken(n.trim().split(/\s+/u)[0] ?? "");
    if (whole) want.add(whole.toLowerCase());
    if (first) want.add(first.toLowerCase());
  }
  if (want.size === 0) return false;
  return extractMentions(body).some((h) => want.has(h.displayName.toLowerCase()));
}

/**
 * True when [body] mentions this account: by its name in the group's member list, or by
 * [extraNames] (your own profile name, which other phones may use).
 */
export function mentionsIdentity(
  body: string,
  members: { displayName: string; identityKeyHex: string }[],
  selfIdentityKeyHex: string,
  extraNames: string[] = [],
): boolean {
  const self = selfIdentityKeyHex.toLowerCase();
  const names = members.filter((m) => m.identityKeyHex.toLowerCase() === self).map((m) => m.displayName);
  return mentionsAnyName(body, [...names, ...extraNames]);
}

export function mentionsAnyone(
  body: string,
  memberNames: string[],
): { displayName: string; identityKeyHex?: string }[] {
  const out: { displayName: string }[] = [];
  const seen = new Set<string>();
  for (const name of memberNames) {
    const key = name.toLowerCase();
    if (!seen.has(key) && mentionsAnyName(body, [name])) {
      seen.add(key);
      out.push({ displayName: name });
    }
  }
  return out;
}
